import api from './api';

// 权限管理相关类型定义
export interface Permission {
  id: number;
  permission_code: string;
  permission_name: string;
  resource_type: string;
  action: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export interface PermissionTree {
  key: string;
  title: string;
  children: Array<{
    key: string;
    title: string;
  }>;
}

export interface UserPermissions {
  user_id: number;
  username: string;
  role: string;
  permissions: string[];
}

export interface PermissionCheckResponse {
  has_permission: boolean;
}

export interface BatchPermissionCheckResponse {
  permissions: Record<string, boolean>;
}

export interface Role {
  id: number;
  role_code: string;
  role_name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export interface RoleDetail {
  role: Role;
  permission_ids: number[];
}

export interface MenuPermissions {
  menu_permissions: string[];
}

// 权限管理API服务
export const permissionService = {
  // 获取权限列表
  getPermissions: async (): Promise<Permission[]> => {
    const response = await api.post('/permissions/list');
    return response.data?.list || [];
  },

  // 获取权限树形结构
  getPermissionTree: async (): Promise<PermissionTree[]> => {
    const response = await api.post('/permissions/tree');
    return response.data?.tree || [];
  },

  // 获取用户权限
  getUserPermissions: async (targetUserId?: number): Promise<UserPermissions> => {
    const response = await api.post('/permissions/user', { target_user_id: targetUserId });
    return response.data.data || { user_id: 0, username: '', role: '', permissions: [] };
  },

  // 检查单个权限
  checkPermission: async (permissionCode: string): Promise<PermissionCheckResponse> => {
    const response = await api.post('/permissions/check', { permission_code: permissionCode });
    return response.data.data || { has_permission: false };
  },

  // 批量检查权限
  checkPermissions: async (permissionCodes: string[]): Promise<BatchPermissionCheckResponse> => {
    const response = await api.post('/permissions/check-batch', { permission_codes: permissionCodes });
    return response.data.data || { permissions: {} };
  },

  // 获取菜单权限
  getMenuPermissions: async (): Promise<MenuPermissions> => {
    const response = await api.post('/auth/menu-permissions');
    return response.data.data || { menu_permissions: [] };
  },

  // 获取角色列表
  getRoles: async (): Promise<Role[]> => {
    const response = await api.post('/roles/list');
    return response.data?.list || [];
  },

  // 获取角色详情
  getRoleDetail: async (roleId: number): Promise<RoleDetail> => {
    const response = await api.post('/roles/detail', { role_id: roleId });
    return response.data || { role: {} as Role, permission_ids: [] };
  },

  // 更新角色权限
  updateRolePermissions: async (roleId: number, permissionIds: number[]): Promise<void> => {
    await api.post('/roles/permissions/update', {
      role_id: roleId,
      permission_ids: permissionIds,
    });
  },

  // 获取角色默认权限
  getDefaultPermissions: async (roleCode: string): Promise<string[]> => {
    const response = await api.post('/roles/default-permissions', { role_code: roleCode });
    return response.data?.permissions || [];
  },

  // 重置角色权限
  resetRolePermissions: async (roleId: number): Promise<void> => {
    await api.post('/roles/reset-permissions', { role_id: roleId });
  },
}; 